/*
 * The application's vocabulary, in one place.
 *
 * These values are KEYS, not labels: the record stores `platform_team`, the CRM groups on it,
 * and the sentence a human reads is looked up here. Every one of them is validated on the
 * server against the same list (`APPLICATION_CHOICE_FIELDS` in backend/server.py), so a value
 * that is not here is a value that will be refused.
 *
 * Written when the answers became editable, which is the point at which the copies stopped
 * being survivable: an option this file offers and the server does not is a form that fails
 * on save, and a label only one screen knows about is an admin decoding `other_b2c`.
 *
 * The wording is the APPLICANT'S, word for word from the form they filled in — "We bill hours
 * only", not "Bills hours only". Both ends read it: the partner is editing their own answer and
 * must recognise it, and an admin is reading a quote of what the partner said. A third-person
 * rewrite served neither, and it made the partner's own Profile ask what "Their own platform"
 * meant.
 *
 * The register and continue forms still carry their own copies. Those are browser-verified and
 * left alone deliberately; when one of them is next touched, it should come here.
 *
 * ⚠ ARRAYS OF PAIRS, NOT OBJECTS, and it matters. These were plain objects until a browser pass
 * showed "None yet" sitting at the TOP of the client-count list — JavaScript hoists integer-like
 * keys to the front of an object whatever order they are written in, and `'0'` is one. Quoting
 * does not help. The one answer we least want leading the list led it. An array cannot be
 * reordered behind your back.
 */

/** The lookup half: a stored key, as a sentence. */
const labelsOf = (pairs) => Object.fromEntries(pairs);

/** What kind of business they are. Descriptive, and NOT `partner_type` — that name belongs to
 *  the retired commercial classification (affiliate / referral / solution) which still has a
 *  live reader on the promo-code page. */
export const BUSINESS_TYPE = [
  ['agency', 'An agency'],
  ['tech_partner', 'A technology partner'],
  ['freelancer', 'An automation freelancer'],
  ['consultant', 'A consultant'],
];
export const BUSINESS_TYPE_LABELS = labelsOf(BUSINESS_TYPE);

/** The single most useful answer we hold: it is the asset, and it is the one that goes stale
 *  fastest. Buckets, never a number. */
export const CLIENT_COUNT = [
  ['20+', '20 or more'],
  ['10-19', '10 to 19'],
  ['5-9', '5 to 9'],
  ['3-4', '3 or 4'],
  ['1-2', '1 or 2'],
  ['0', 'None yet'],
];
export const CLIENT_COUNT_LABELS = labelsOf(CLIENT_COUNT);

/** What client work runs on today: what we would be replacing. Many answers. */
export const RUNS_ON = [
  ['crm', 'A CRM (Zoho, HubSpot, Salesforce)'],
  ['workflow_builder', 'A workflow builder (n8n, Make, Zapier)'],
  ['marketing_platform', 'A marketing platform (MoEngage, CleverTap, WebEngage, Klaviyo)'],
  ['own_platform', 'Our own platform'],
  ['sheets', 'Google Sheets or Excel'],
  ['nothing', 'Nothing yet'],
  ['other', 'Something else'],
];
export const RUNS_ON_LABELS = labelsOf(RUNS_ON);

export const TEAM_SIZE = [
  ['1-4', '1 to 4'],
  ['5-20', '5 to 20'],
  ['21-50', '21 to 50'],
  ['51-200', '51 to 200'],
  ['200+', 'More than 200'],
];
export const TEAM_SIZE_LABELS = labelsOf(TEAM_SIZE);

export const RETAINER_SHARE = [
  ['gt75', 'More than 75%'],
  ['50-75', '50 to 75%'],
  ['25-49', '25 to 49%'],
  ['lt25', 'Under 25%, mostly project work'],
];
export const RETAINER_SHARE_LABELS = labelsOf(RETAINER_SHARE);

export const DELIVERY_CAPABILITY = [
  ['platform_team', 'A dedicated ops team already runs a CRM or automation platform for clients'],
  ['campaign_ops', 'We run campaigns and ad ops, but no platform of our own'],
  ['founder_led', 'Founder-operated, with some tooling'],
  ['outsourced', 'We outsource all delivery'],
];
export const DELIVERY_CAPABILITY_LABELS = labelsOf(DELIVERY_CAPABILITY);

export const VERTICAL_FIT = [
  ['majority', 'More than half the book'],
  ['some', 'Some of the book'],
  ['other_b2c', 'Other B2C'],
  ['b2b', 'Mostly B2B'],
];
export const VERTICAL_FIT_LABELS = labelsOf(VERTICAL_FIT);

export const COMMERCIAL_POSTURE = [
  ['resells', 'We resell or mark up licences'],
  ['bundles', 'We bundle tools into the retainer'],
  ['client_direct', 'The client buys direct and we operate it'],
  ['hours_only', 'We bill hours only'],
];
export const COMMERCIAL_POSTURE_LABELS = labelsOf(COMMERCIAL_POSTURE);

/**
 * The editable questions, in the order they are worth reading.
 *
 * Client count leads because it is the thing that actually matters. What follows describes the
 * book; the plain fields after it describe the business. One list, so the partner's form and
 * the admin's form cannot drift into asking different questions.
 */
export const APPLICATION_QUESTIONS = [
  { field: 'client_count', label: 'Clients today', options: CLIENT_COUNT },
  { field: 'business_type', label: 'What you are', options: BUSINESS_TYPE },
  { field: 'runs_on', label: 'What you run client work on', options: RUNS_ON, multi: true },
  { field: 'team_size', label: 'Team size', options: TEAM_SIZE },
  { field: 'retainer_share', label: 'On retainer', options: RETAINER_SHARE },
  { field: 'delivery_capability', label: 'How delivery works', options: DELIVERY_CAPABILITY },
  { field: 'vertical_fit', label: 'Clients selling to consumers', options: VERTICAL_FIT },
  { field: 'commercial_posture', label: 'How software reaches your clients', options: COMMERCIAL_POSTURE },
];

/** The open answers. Free text, because the point of asking is the one we had not thought of. */
export const APPLICATION_TEXT_FIELDS = [
  { field: 'company_name', label: 'Business name' },
  { field: 'website', label: 'Website' },
  { field: 'linkedin_url', label: 'LinkedIn' },
  { field: 'designation', label: 'Your role' },
  { field: 'city', label: 'City' },
  { field: 'state', label: 'State' },
];

/** A stored key, as a sentence. Falls back to the key itself: a record written before an
 *  option existed should read oddly rather than read as empty. */
export const answerLabel = (options, value) =>
  (value ? labelsOf(options)[value] || value : '—');
